import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL } from "../../config";
import { showSuccess } from "../../utils/dialog";
import "../../Styles/AccessibilityStndCustmer.css";

const WorkPref = () => {
  const navigate = useNavigate();
  const itemId = "work-preferences";
  const [divisions, setDivisions] = useState([]);
  const [divisionsLoading, setDivisionsLoading] = useState(true);
  const [selectedDivision, setSelectedDivision] = useState(null);
  const [divisionSkills, setDivisionSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [selectedDivisions, setSelectedDivisions] = useState([]);

  const [formData, setFormData] = useState(() => {
    const saved = localStorage.getItem(`form_${localStorage.getItem("accountId")}_${itemId}`);
    const initial = saved ? JSON.parse(saved) : {};
    return {
      availability: Array.isArray(initial.availability) ? initial.availability : [],
      skills: Array.isArray(initial.skills) ? initial.skills : [],
    };
  });

  const skillsToShow = divisionSkills.filter(skill => {
    const skillDivisionIds = skill.division_ids.split(",").map(id => parseInt(id));
    return selectedDivisions.some(id => skillDivisionIds.includes(id));
  });

  const [divisionError, setDivisionError] = useState("");

  useEffect(() => {
    const saved = localStorage.getItem(`form_${localStorage.getItem("accountId")}_${itemId}`);
    if (saved) {
      const raw = JSON.parse(saved);
      setFormData({
        availability: Array.isArray(raw.availability) ? raw.availability : [],
        skills: Array.isArray(raw.skills) ? raw.skills : [],
      });
    }
  }, []);

  useEffect(() => {
    const accountId = localStorage.getItem("accountId");
    const storedStatusKey = `status_${accountId}_${itemId}`;

    const anySelected = formData.availability && formData.availability.length > 0;
    const currentDraftStatus = anySelected ? "in-progress" : "incomplete";

    const previousStatus = localStorage.getItem(storedStatusKey);

    let statusToSave = currentDraftStatus;
    if (previousStatus === "complete") {
      statusToSave = anySelected ? "complete" : "incomplete";
    }

    localStorage.setItem(storedStatusKey, statusToSave);
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));

    // When skills or divisions change, update required certifications
    if (accountId) {
      const selectedSkillIds = divisionSkills
        .filter(s => formData.skills.includes(s.name))
        .map(s => s.id);

      loadRequiredCertifications(selectedDivisions, selectedSkillIds);
    }
  }, [formData, selectedDivisions]);

  useEffect(() => {
    const loadDivisions = async () => {
      try {
        setDivisionsLoading(true);
        const res = await fetch(`${BASE_URL}/api/admin/divisions-list?page=1&limit=100`, {
          credentials: "include",
        });

        if (!res.ok) {
          throw new Error(`Failed to load divisions (${res.status})`);
        }

        const text = await res.text();
        let json;
        try {
          json = JSON.parse(text);
        } catch (parseErr) {
          console.error("Failed to parse response:", parseErr, "Response text:", text);
          throw new Error("Invalid response from server");
        }

        console.log("Divisions loaded:", json);
        setDivisions(json.data || []);
      } catch (err) {
        console.error("Failed to load divisions", err);
        setDivisions([]);
      } finally {
        setDivisionsLoading(false);
      }
    };

    loadDivisions();
  }, []);

  useEffect(() => {
    if (divisions.length > 0 && formData.availability.length > 0) {
      const restoredIds = divisions
        .filter((div) => formData.availability.includes(div.division_name))
        .map((div) => div.id);

      if (restoredIds.length > 0) {
        setSelectedDivisions(restoredIds);
        setSelectedDivision(formData.availability[0]);
        loadSkillsForDivision(restoredIds);
      }
    }
  }, [divisions]);

  const loadSkillsForDivision = async (divisionIds) => {
    try {
      setSkillsLoading(true);
      const responses = await Promise.all(
        divisionIds.map(id => fetch(`${BASE_URL}/api/admin/skills?page=1&limit=100&division=${id}`))
      );

      const allSkills = (await Promise.all(responses.map(r => r.json())))
        .flatMap(d => d.data || []);

      const unique = Array.from(new Map(allSkills.map(s => [s.id, s])).values());
      setDivisionSkills(unique);
    } catch (err) {
      console.error("Failed to load skills", err);
    } finally {
      setSkillsLoading(false);
    }
  };

  const loadRequiredCertifications = async (divIds, skillIds) => {
    try {
      const accountId = localStorage.getItem("accountId");
      if (!accountId) return;

      // Filter out invalid IDs (NaN, null, undefined, etc.)
      const validDivIds = (divIds || []).filter(id => {
        const parsed = Number(id);
        return Number.isFinite(parsed) && parsed > 0;
      });
      
      const validSkillIds = (skillIds || []).filter(id => {
        const parsed = Number(id);
        return Number.isFinite(parsed) && parsed > 0;
      });

      console.log('[WorkPref] Loading certifications with IDs:', { 
        originalDivIds: divIds, 
        validDivIds,
        originalSkillIds: skillIds,
        validSkillIds 
      });

      // Only make the request if we have valid IDs
      if (validDivIds.length === 0 && validSkillIds.length === 0) {
        console.log('[WorkPref] No valid IDs, clearing certifications');
        localStorage.setItem(`certs_${accountId}_work-preferences`, JSON.stringify([]));
        return;
      }

      const res = await axios.get(`${BASE_URL}/api/registration/required-certifications`, {
        params: {
          divisions: validDivIds.join(','),
          skills: validSkillIds.join(',')
        }
      });

      if (res.data && res.data.data) {
        console.log('[WorkPref] Loaded certifications:', res.data.data);
        localStorage.setItem(`certs_${accountId}_work-preferences`, JSON.stringify(res.data.data));
      }
    } catch (err) {
      console.error("Failed to load required certifications", err);
    }
  };

  const handleSubmit = () => {
    if (formData.availability.length === 0) {
      setDivisionError("This field is required");
      return;
    }

    setDivisionError("");
    const anySelected = formData.availability && formData.availability.length > 0;
    const newStatus = anySelected ? "complete" : "incomplete";
    const accountId = localStorage.getItem("accountId");
    localStorage.setItem(`form_${accountId}_${itemId}`, JSON.stringify(formData));
    localStorage.setItem(`status_${accountId}_${itemId}`, newStatus);

    if (accountId && anySelected) {
      const payload = {
        status: newStatus,
        formData,
        selectedDivisions // Include IDs for backend mapping
      };
      axios.post(`${BASE_URL}/api/registration/${accountId}/items/${itemId}/complete`, payload)
        .catch(err => console.error("Failed to save item data:", err))
        .finally(() => {
          axios.post(`${BASE_URL}/api/registration/${accountId}/status/${itemId}`, {
            status: newStatus,
            formData: {}
          }).catch(err => console.error("Failed to sync status:", err)).finally(() => {
            showSuccess("Form completed successfully!");
            navigate("/register");
          });
        });
    } else {
      navigate("/register");
    }
  };

  const handleBack = () => {
    navigate("/register");
  };

  const handleDivisionSelect = (divisionName, divisionId) => {
    const isSelected = formData.availability.includes(divisionName);

    let newAvailability = [];
    let newSelectedDivisions = [];

    if (isSelected) {
      newAvailability = formData.availability.filter(d => d !== divisionName);
      newSelectedDivisions = selectedDivisions.filter(id => id !== divisionId);
    } else {
      newAvailability = [...formData.availability, divisionName];
      newSelectedDivisions = [...selectedDivisions, divisionId];
    }

    setSelectedDivision(newAvailability.length > 0 ? divisionName : null);
    setFormData(prev => ({ ...prev, availability: newAvailability }));
    setSelectedDivisions(newSelectedDivisions);

    if (divisionSkills.length > 0) {
      const remainingSkills = divisionSkills.filter(skill => {
        const skillDivisionIds = skill.division_ids.split(",").map(id => parseInt(id));
        return newSelectedDivisions.some(id => skillDivisionIds.includes(id));
      }).map(skill => skill.name);

      setFormData(prev => ({
        ...prev,
        skills: prev.skills.filter(s => remainingSkills.includes(s))
      }));
    }

    if (newSelectedDivisions.length === 0) {
      setDivisionSkills([]);
    } else {
      loadSkillsForDivision(newSelectedDivisions);
    }
  };

  const handleSkillSelect = (skillName) => {
    setFormData((prev) => {
      const exists = prev.skills.includes(skillName);
      return {
        ...prev,
        skills: exists
          ? prev.skills.filter((s) => s !== skillName)
          : [...prev.skills, skillName],
      };
    });
  };

  return (
    <div className="accessibility-form-page">
      <div className="form-container">
        <div className="form-card">
          <header className="form-card-header">
            <h1 className="company-logo">talent</h1>
            <p className="company-name">employment inc.</p>
            <h2 className="form-title">
              Work Preferences
            </h2>
          </header>

          <section className="form-card-content">
            <p className="form-intro">
              Please select the division(s) you are willing to work and the relevant skills that you may have. Once you select your division, please also upload your relevant certifications.
            </p>
            <label className="test-sm text-gray-800 font-semibold">Division</label>
            {divisionsLoading && <p style={{ marginLeft: "10px" }}>Loading divisions...</p>}
            {!divisionsLoading && divisions.length === 0 && (
              <p style={{ marginLeft: "10px", color: "#dc2626" }}>No divisions available.</p>
            )}
            {!divisionsLoading && divisions.length > 0 && (
              <div className="week-days" style={{ marginTop: "5px", marginLeft: "10px" }}>
                {divisions.map((division) => (
                  <label key={division.id}>
                    <input
                      type="checkbox"
                      name="division"
                      value={division.division_name}
                      checked={formData.availability.includes(division.division_name)}
                      onChange={() => handleDivisionSelect(division.division_name, division.id)}
                      className="mr-2"
                    />
                    {division.division_name}
                  </label>
                ))}
              </div>
            )}

            {divisionError && (
              <div className="error-text" style={{ color: "#dc2626", marginBottom: 6 }}>
                {divisionError}
              </div>
            )}

            {selectedDivision && (
              <div style={{ marginTop: "20px" }}>
                <label className="test-sm text-gray-800 font-semibold">Skills</label>

                {skillsLoading && <p>Loading skills...</p>}

                {!skillsLoading && divisionSkills.length === 0 && (
                  <p>No skills available for this division.</p>
                )}

                {!skillsLoading && divisionSkills.length > 0 && (
                  <div className="week-days" style={{ marginTop: "5px", marginLeft: "10px" }}>
                    {skillsToShow.map(skill => (
                      <label key={skill.id}>
                        <input
                          type="checkbox"
                          value={skill.name}
                          checked={formData.skills.includes(skill.name)}
                          onChange={() => handleSkillSelect(skill.name)}
                          className="mr-2"
                        />
                        {skill.name}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

          </section>
          <div className="form-buttons">
            <button type="button" onClick={handleBack}>Back</button>
            <button type="submit" onClick={handleSubmit}>Submit</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkPref;